import isEqual from 'lodash/isEqual';

import pipelineNotInCluster from 'api/pipelines/utils/determinePipelineStatus/shared/pipelineNotInCluster';
import buildStagedIntegrationsMap from './buildStagedIntegrationsMap';
import buildDeployedIntegrationsMap from './buildDeployedIntegrationsMap';
import getDeployedPipelineVariableValue from './getDeployedPipelineVariableValue';

export default function isFullyDeployed(ionPipeline, controlPipeline) {
  if (pipelineNotInCluster(controlPipeline)) {
    return false;
  }

  const stagedIntegrationsMap = buildStagedIntegrationsMap(ionPipeline.pipelineIntegrations);
  const deployedIntegrationsMap = buildDeployedIntegrationsMap(controlPipeline.pipeline.integrations);

  // Deployed integrations not represented in the staged map are CLI managed and are intentionally
  // ignored here — they should not block the pipeline from being considered fully deployed.
  const isFullyDeployed = Object.entries(stagedIntegrationsMap).every(([stagedIntegrationKey, stagedIntegration]) => {
    // Check that all integrations exist on the deployed pipeline
    const deployedIntegration = deployedIntegrationsMap[stagedIntegrationKey];
    if (!deployedIntegration) {
      return false;
    }

    // Check that all deployed and staged integrations share the same disabled state
    const disabledStatesMatch = isEqual(stagedIntegration.disabled, deployedIntegration.disabled);
    if (!disabledStatesMatch) {
      return false;
    }

    // Check that all deployed and staged integrations share the same revision ID
    const revisionIdsMatch = isEqual(stagedIntegration.revisionId, deployedIntegration.revisionId);
    if (!revisionIdsMatch) {
      return false;
    }

    // Check that all deployed and staged integrations share the same revision number
    const revisionNumbersMatch = isEqual(stagedIntegration.revisionNumber, deployedIntegration.revisionNumber);
    if (!revisionNumbersMatch) {
      return false;
    }

    // Check whether any integrations are marked for deletion
    const stagedForDelete = stagedIntegration.stagedForDelete;
    if (stagedForDelete) {
      return false;
    }

    // Check that all deployed and staged integrations share the same consent category
    const consentCategoryCodesMatch = isEqual(
      stagedIntegration.consentCategoryCode,
      deployedIntegration.consentCategoryCode
    );
    if (!consentCategoryCodesMatch) {
      return false;
    }

    // Compare consent modes. Both sides are guaranteed coerced:
    // - staged comes from Hasura (FK to consent_modes — only ACE/EXTERNAL, never UNSPECIFIED).
    // - deployed comes from buildDeployedIntegrationsMap / resolveDeployedConsent (UNSPECIFIED→ACE applied).
    const consentModesMatch = isEqual(stagedIntegration.consentMode, deployedIntegration.consentMode);
    if (!consentModesMatch) {
      return false;
    }

    // Check that all deployed and staged integrations share the same pipeline vars
    const allIntegrationVarsRepresented = Object.entries(stagedIntegration.pipelineVars).every(
      ([pipelineVarKey, pipelineVarValue]) => {
        const deployedIntegrationVarValue = getDeployedPipelineVariableValue({
          variableKey: pipelineVarKey,
          deployedIntegration,
        });
        return isEqual(pipelineVarValue, deployedIntegrationVarValue);
      }
    );
    if (!allIntegrationVarsRepresented) {
      return false;
    }

    return true;
  });

  return isFullyDeployed;
}
